import Swal from "sweetalert2";
import ApiService, { HttpMethod } from "../../../commons/api/api_service";
import BaseResponse from "../../../commons/api/base_response";
import { Endpoints } from "../../../commons/api/endpoints";
import { ProviderVM } from "../../../commons/base/providers/view_model";
import { Pageable, Paging } from "../../../commons/models/Page";
import {
    ShowErrorViewModel,
    ShowSnackbarModel,
} from "../../../commons/scaffold/scaffold";
import StringUtils from "../../../commons/utils/string_utils";
import { SearchMasterItemRequest } from "../models/master-item-request";
import { MasterItemModel } from "../models/master-item-response";

export default class MasterItemDashboardViewModel extends ProviderVM {
    // UI States
    isLoading: boolean = false;
    isShowFilterSidebar: boolean = false;
    errorMessage: string = "";
    showSnackbar?: ShowSnackbarModel;
    showErrorView?: ShowErrorViewModel;
    materialTypeOptions: string[] = [
        "All",
        "Raw Material",
        "Bumbu",
        "Sub Assembly",
        "Assembly",
        "Menu Prepare",
        "Menu",
        "Packaging Raw",
        "Packaging Menu",
        "Supplies",
    ];
    itemCategoriesOptions: string[] = [
        "None",
        "BASAHAN",
        "BASAHAN_RAW_MAT",
        "KERINGAN_PACKAGING",
        "KERINGAN_RAW_MAT",
        "KERINGAN_PRODUKSI",
        "BASAHAN_PRODUKSI",
        "KERINGAN_BUMBU",
        "KERINGAN",
    ];
    whStorageTypeOptions: string[] = [
        "None",
        "Chiller",
        "Freezer",
        "Suhu Ruang",
    ];
    inventoryForms = {
        transferItem: "Transfer Item",
        stockOpname: "Stock Opname",
        waste: "Waste",
        audit: "Audit",
        produksi: "Produksi",
        procurement: "Procurement"
    };
    soCategories: string[] = [
        "LAINNYA",
        "MINUMAN",
        "PACKAGING",
        "PREPARE",
        "RAW_MAT_FRYER",
        "RAW_MAT_MIE",
        "SAYUR",
        "BASAHAN",
        "KERINGAN"
    ];
    soSuppliesCategories: string[] = [
        "KONDIMEN",
        "SUPPLIES"
    ];
    wasteCategories: string[] = [
        "KONDIMEN",
        "LAINNYA",
        "MENU",
        "MINUMAN",
        "PACKAGING",
        "PREPARE",
        "RAW_MAT_FRYER",
        "RAW_MAT_MIE",
        "SAYUR",
        "BASAHAN",
        "KERINGAN"
    ];

    // Table and search inputs
    searchKeyword?: string;
    searchMaterialType?: string;
    masterItemList: MasterItemModel[] = [];
    paging: Paging = {
        currentPage: 1,
        size: 20,
        totalPage: 1,
    };

    // Modal properties
    isShowModal: boolean = false;
    modalAction: string = "CREATE";
    modalMasterItemId?: number;
    modalItemMaterialType?: string = "Raw Material";
    modalItemName?: string;
    modalSmallUnit?: string;
    modalBigUnit?: string;
    modalBigToSmallUnitConversion?: number;
    modalIsActive: boolean = false;
    modalIsCkItem: boolean = false;
    modalIsOutletItem: boolean = false;
    modalIsWHItem: boolean = false;
    modalIsSOSuppliesItem: boolean = false;
    modalIsPackagingItem: boolean = false;
    modalIsBumbuItem: boolean = false;
    modalIsSODaily: boolean = false;
    modalIsSODailyWH: boolean = false;
    modalWHStorageType?: string;
    modalItemCategory?: string;
    modalYieldNum?: number;
    modalSalesPrice?: number;
    selectedInventoryForms: Set<string> = new Set();
    selectedSoCategory?: string;
    selectedWasteCategory?: string;
    selectedSoSuppliesCategory?: string;

    constructor() {
        super();
        this.setup();
    }

    setup() {
        this.getMasterItemList();
    }

    // UI State Setters
    reset() {
        this.emit(() => {
            this.isLoading = false;
            this.searchKeyword = undefined;
            this.masterItemList = [];
        });
    }

    setIsLoading(isLoading: boolean) {
        this.emit(() => {
            this.isLoading = isLoading;
        });
    }

    setIsShowFilterSidebar(show: boolean) {
        this.emit(() => {
            this.isShowFilterSidebar = show;
        });
    }

    setErrorMessage(errorMessage: string) {
        this.emit(() => {
            this.errorMessage = errorMessage;
        });
    }

    setShowErrorView(showErrorView?: ShowErrorViewModel) {
        this.emit(() => {
            this.showErrorView = showErrorView;
        });
    }

    // Search Inputs
    setSearchKeyword(keyword?: string) {
        this.emit(() => {
            this.searchKeyword = keyword;
        });
    }

    setSearchMaterialType(value?: string) {
        this.emit(() => {
            this.searchMaterialType = value;
        });
    }

    // Modal Setters
    openModal() {
        this.emit(() => {
            this.isShowModal = true;
        });
    }

    resetModal() {
        this.emit(() => {
            this.isShowModal = false;
            this.modalAction = "CREATE";
            this.modalMasterItemId = undefined;
            this.modalItemMaterialType = "Raw Material";
            this.modalItemName = undefined;
            this.modalSmallUnit = undefined;
            this.modalBigUnit = undefined;
            this.modalBigToSmallUnitConversion = undefined;
            this.modalIsActive = false;
            this.modalIsCkItem = false;
            this.modalIsOutletItem = false;
            this.modalIsWHItem = false;
            this.modalIsSOSuppliesItem = false;
            this.modalIsPackagingItem = false;
            this.modalIsBumbuItem = false;
            this.modalIsSODaily = false;
            this.modalIsSODailyWH = false;
            this.modalWHStorageType = undefined;
            this.modalItemCategory = undefined;
            this.modalYieldNum = undefined;
            this.modalSalesPrice = undefined;
            this.selectedSoCategory = undefined;
            this.selectedWasteCategory = undefined;
            this.selectedInventoryForms.clear();
        });
    }

    setIsShowModal(value: boolean) {
        this.emit(() => {
            this.isShowModal = value;
        });
    }

    setModalAction(value: string) {
        this.emit(() => {
            this.modalAction = value;
        });
    }

    setModalMasterItemId(id: number) {
        this.emit(() => {
            this.modalMasterItemId = id;
        });
    }

    setModalItemMaterialType(value?: string) {
        this.emit(() => {
            this.modalItemMaterialType = value;
            if (value !== "Menu") {
                this.setModalSalesPrice(0);
            }
        });
    }

    setModalItemName(value?: string) {
        this.emit(() => {
            this.modalItemName = value;
        });
    }

    setModalSmallUnit(value?: string) {
        this.emit(() => {
            this.modalSmallUnit = value;
        });
    }

    setModalBigUnit(value?: string) {
        this.emit(() => {
            this.modalBigUnit = value;
        });
    }

    setModalBigToSmallUnitConversion(value?: number) {
        this.emit(() => {
            this.modalBigToSmallUnitConversion = value;
        });
    }

    setModalIsActive(value: boolean) {
        this.emit(() => {
            this.modalIsActive = value;
        });
    }

    setModalIsCkItem(value: boolean) {
        this.emit(() => {
            this.modalIsCkItem = value;
        });
    }

    setModalIsOutletItem(value: boolean) {
        this.emit(() => {
            this.modalIsOutletItem = value;
        });
    }

    setModalIsWHItem(value: boolean) {
        this.emit(() => {
            this.modalIsWHItem = value;
        });
    }

    setModalIsSOSuppliesItem(value: boolean) {
        this.emit(() => {
            this.modalIsSOSuppliesItem = value;
        });
    }

    setModalIsPackagingItem(value: boolean) {
        this.emit(() => {
            this.modalIsPackagingItem = value;
        });
    }

    setModalIsBumbuItem(value: boolean) {
        this.emit(() => {
            this.modalIsBumbuItem = value;
        });
    }

    setModalIsSODaily(value: boolean) {
        this.emit(() => {
            if (value) {
                this.selectedInventoryForms.add("SO Daily");
            } else {
                this.selectedInventoryForms.delete("SO Daily");
            }
            this.modalIsSODaily = value;
        });
    }

    setModalIsSODailyWH(value: boolean) {
        this.emit(() => {
            if (value) {
                this.selectedInventoryForms.add("SO Daily WH");
            } else {
                this.selectedInventoryForms.delete("SO Daily WH");
            }
            this.modalIsSODailyWH = value;
        });
    }

    setModalWHStorageType(value?: string) {
        this.emit(() => {
            this.modalWHStorageType = value;
        });
    }

    setModalItemCategory(value?: string) {
        this.emit(() => {
            this.modalItemCategory = value;
        });
    }

    setModalYieldNum(value?: number) {
        this.emit(() => {
            this.modalYieldNum = value;
        });
    }

    setModalSalesPrice(value?: number) {
        this.emit(() => {
            this.modalSalesPrice = value;
        });
    }

    populateModal(id: number) {
        this.setIsLoading(true);
        ApiService.fetch(
            `${Endpoints.getMasterItemDetail}/${id}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (response: BaseResponse<MasterItemModel>) => {
                    this.setIsLoading(false);
                    if (response?.data) {
                        const masterItemData = response.data;

                        this.setModalMasterItemId(masterItemData.id);
                        this.setModalItemMaterialType(
                            StringUtils.snakeCaseToNormal(
                                masterItemData.materialType!
                            )
                        );
                        this.setModalItemName(masterItemData.item);
                        this.setModalSmallUnit(masterItemData.smallUnit);
                        this.setModalBigUnit(masterItemData.bigUnit);
                        this.setModalBigToSmallUnitConversion(
                            masterItemData.bigToSmallUnitConversion
                        );
                        this.setModalIsActive(masterItemData.isActive);
                        this.setModalIsCkItem(masterItemData.isCkItem);
                        this.setModalIsOutletItem(masterItemData.isOutletItem);
                        this.setModalIsWHItem(masterItemData.isWhItem);
                        this.setModalIsSOSuppliesItem(
                            masterItemData.isSoSuppliesItem
                        );
                        this.setModalIsPackagingItem(
                            masterItemData.isPackaging ?? false
                        );
                        this.setModalIsBumbuItem(masterItemData.isBumbuItem);
                        this.setModalWHStorageType(
                            masterItemData.whStorageType
                        );
                        this.setModalItemCategory(masterItemData.itemCategory);
                        this.setModalYieldNum(masterItemData.yieldNum);
                        this.setModalSalesPrice(masterItemData.salesPrice);
                        this.setSelectedSoCategory(masterItemData.masterItemInventoryCategory.soRegularCategory);
                        this.setSelectedSoSuppliesCategory(masterItemData.masterItemInventoryCategory.soSuppliesCondimentCategory);
                        this.setSelectedWasteCategory(masterItemData.masterItemInventoryCategory.wasteCategory);

                        if (masterItemData.categories) {
                            let isSODaily = masterItemData.categories.indexOf("SO Daily") != -1;
                            this.setModalIsSODaily(isSODaily);
                            this.setModalIsSODailyWH(masterItemData.categories.indexOf("SO Daily WH") != -1);
                            this.selectedInventoryForms = new Set(masterItemData.categories);
                        }
                    }
                },
                onFailure: (error) => {
                    console.log(error);
                    this.setIsLoading(false);
                    this.setErrorMessage(
                        "Failed getting Master Item data, please try again"
                    );
                    this.setShowErrorView({
                        action: () => {
                            this.resetModal();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                },
            }
        );
    }

    isInputValid() {
        return (
            this.modalItemMaterialType !== undefined &&
            this.modalItemName !== undefined &&
            this.modalSmallUnit !== undefined &&
            this.modalBigToSmallUnitConversion !== undefined
        );
    }

    submitMasterItemData() {
        if (!this.isInputValid()) {
            this.setErrorMessage("Please check required fields!");
            this.setShowErrorView({
                action: () => {
                    this.setShowErrorView(undefined);
                },
                message: this.errorMessage,
            });
            return;
        }

        ApiService.fetch(
            this.modalAction === "CREATE"
                ? Endpoints.createMasterItem
                : Endpoints.updateMasterItem,
            {
                httpMethod:
                    this.modalAction === "CREATE"
                        ? HttpMethod.post
                        : HttpMethod.put,
                parameters: {
                    id: this.modalMasterItemId,
                    materialType: this.modalItemMaterialType,
                    item: this.modalItemName,
                    smallUnit: this.modalSmallUnit,
                    bigUnit: this.modalBigUnit,
                    bigToSmallUnitConversion:
                        this.modalBigToSmallUnitConversion,
                    isActive: this.modalIsActive,
                    isCkItem: this.modalIsCkItem,
                    isOutletItem: this.modalIsOutletItem,
                    isWhItem: this.modalIsWHItem,
                    isSoSuppliesItem: this.modalIsSOSuppliesItem,
                    isPackaging: this.modalIsPackagingItem,
                    isBumbuItem: this.modalIsBumbuItem,
                    whStorageType: this.modalWHStorageType,
                    itemCategory: this.modalItemCategory,
                    yieldNum: this.modalYieldNum,
                    salesPrice: this.modalSalesPrice,
                    selectedInventoryForms: [...this.selectedInventoryForms.values()],
                    selectedSoCategory: this.selectedSoCategory,
                    selectedSoSuppliesCategory: this.selectedSoSuppliesCategory,
                    selectedWasteCategory: this.selectedWasteCategory,
                },
                // success or client error
                onSuccess: (response: BaseResponse<MasterItemModel>) => {
                    this.setIsLoading(false);
                    if ([201, 200].includes(response.code)) {
                        this.showSnackbar = new ShowSnackbarModel({
                            message:
                                this.modalAction === "CREATE"
                                    ? "Success Input"
                                    : "Success Update",
                            type: "normal",
                        });

                        if (response.data?.materialType === "menu") {
                            Swal.fire({
                                title: "PERHATIAN",
                                text: `Harap Masukkan Packaging untuk Menu Baru : ${response.data?.item}!`,
                                icon: "warning",
                                confirmButtonColor: "#3085d6",
                                confirmButtonText: "Siap!",
                            }).then((_) => {
                                window.location.href = "/menu-packaging";
                            });
                        } else {
                            this.resetModal();
                            this.getMasterItemList();
                        }
                    } else {
                        this.setErrorMessage(response.message);
                        this.setShowErrorView({
                            action: () => {
                                this.resetModal();
                                this.setShowErrorView(undefined);
                                this.setup();
                            },
                            message: this.errorMessage,
                        });
                    }
                },
                onFailure: (error) => {
                    console.log(error);
                    this.setIsLoading(false);
                    this.setErrorMessage(error.message);
                    this.setShowErrorView({
                        action: () => {
                            this.resetModal();
                            this.setShowErrorView(undefined);
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                },
            }
        );
    }

    // Paging
    setPaging(paging: Paging) {
        this.emit(() => {
            this.paging = paging;
        });
    }

    handleChangePage(page: number) {
        this.setPaging({
            currentPage: page,
            size: this.paging?.size || 10,
            totalPage: this.paging?.totalPage!,
        });
        this.getMasterItemList();
    }

    // Fetch data
    getMasterItemList() {
        this.setIsLoading(true);

        const queryParameters: SearchMasterItemRequest = {
            keyword: this.searchKeyword,
            materialType: this.searchMaterialType,
            page: this.paging.currentPage,
            size: this.paging.size,
        };

        ApiService.fetchWithQueryParams(Endpoints.getMasterItemList, {
            httpMethod: HttpMethod.get,
            queryParameters: Object.fromEntries(
                Object.entries(queryParameters).filter(
                    ([_, value]) => value !== undefined
                )
            ),
            onSuccess: (response: BaseResponse<Pageable<MasterItemModel>>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    const masterItemList = response.data!
                        .data as MasterItemModel[];
                    const paging = response.data!.paging as Paging;
                    this.setMasterItemList(masterItemList);
                    this.setPaging(paging);
                } else {
                    this.setErrorMessage(
                        `${
                            response.code === 500
                                ? "Internal Server Error, please report to tech team"
                                : "Bad Request, please refresh or try again"
                        }`
                    );
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.setIsLoading(false);
                this.setErrorMessage(
                    "Failed fetching data, please refresh or try again"
                );
                this.setShowErrorView({
                    action: () => {
                        this.reset();
                        this.setShowErrorView(undefined);
                        this.setErrorMessage("");
                        this.setup();
                    },
                    message: this.errorMessage,
                });
            },
        });
    }

    setMasterItemList(masterItemList: MasterItemModel[]) {
        this.emit(() => {
            this.masterItemList = masterItemList;
        });
    }

    updateSelectedInventoryForms(selection: string, isSelected: boolean) {
        if (isSelected) {
            this.emit(() => {
                this.selectedInventoryForms.add(selection);
            })
        } else {
            this.emit(() => {
                this.selectedInventoryForms.delete(selection);
            })
        }
    }

    setSelectedSoCategory(selection?: string) {
        this.emit(() => {
            this.selectedSoCategory = selection;
        })
    }

    setSelectedSoSuppliesCategory(selection?: string) {
        this.emit(() => {
            this.selectedSoSuppliesCategory = selection;
        })
    }

    setSelectedWasteCategory(selection?: string) {
        this.emit(() => {
            this.selectedWasteCategory = selection;
        })
    }
}
